<script setup>
import useVuelidate from "@vuelidate/core";
import { email, helpers, maxLength, maxValue, minValue, requiredIf } from "@vuelidate/validators";
import { isAfter, subYears } from "date-fns";
import _ from "lodash";
import { computed, createApp, h, onBeforeMount, reactive, ref, toRaw, toRefs, watch } from "vue";
import { useStore } from "vuex";
import ManualAddressForm from "@/components/ExperiencePage/Misc/ManualAddressForm.vue";
import AddressComponent from "@/components/forms/AddressInputComponent/AddressComponent.vue";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import CheckBox from "@/components/forms/SharedComponents/DarkBlueInputs/Checkbox.vue";
import RadioButtons from "@/components/forms/SharedComponents/DarkBlueInputs/RadioButtons.vue";
import SingleSelect from "@/components/forms/SharedComponents/DarkBlueInputs/SingleSelect.vue";
import TextField from "@/components/forms/SharedComponents/DarkBlueInputs/TextField.vue";
import Tooltip from "@/components/forms/SharedComponents/Tooltip.vue";
import { proficiencyLevelsOptions, sessionType } from "@/components/program-manager/sessions/constants.js";
import { useAllowedUniversities } from "@/components/RegistrationProcess/composables/useAllowedUniversities.ts";
import { useLearnerTypes } from "@/components/RegistrationProcess/composables/useLearnerTypes.ts";
import { patchFormioObject } from "@/components/RegistrationProcess/utils/utils";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import HelpIcon from "@/components/shared/icons/HelpIcon.vue";
import SchoolSearchSelect from "@/components/shared/SchoolSearchSelect/index.vue";
import { hasScholarshipElegiblityEnabled } from "@/composables/authorization.js";
import billingAgreement from "@/composables/useBillings.js";
import {
  ACADEMIC_YEARS,
  API_CONNECT_MEMBER,
  GAP_YEAR_STUDENT,
  INVALID_SELECTION,
  OVER_PRICE_2000,
  PAYMENT_PLANS,
} from "@/constants";
import { validEmailAddress } from "@/mixins/customValidators";
import cipCodesService from "@/services/cipCodes";
import entitiesService from "@/services/entities.js";
import { compareStringsInLowerCase } from "@/util/string";
import { useInitialOptionsData } from "../composables/useInitialOptionsData";
import { useLearnerInformationDisplaySettings } from "../composables/useLearnerInformationDisplaySettings";

const props = defineProps({
  // see useApplication.js for application definition
  application: {
    type: Object,
    default: () => {},
    required: true,
  },
  goToNextStep: {
    type: Function,
    default: () => {},
    required: true,
  },
});

const { application } = toRefs(props);
const { initialData: initialOptionsData } = useInitialOptionsData();

const {
  profile,
  profileIsLoading,
  updateProfile,
  applicationId,
  financialAid,
  createFinancialAid,
  updateFinancialAid,
  learnerInformationSubmission,
  saveLearnerInformationSubmission,
  primaryOrder,
  partialUpdateApplicationSubmission,
  session,
} = application.value;

const {
  isUsCollegeStudent,
  hasHomeInstitution,
  showLearnerTypes,
  showSchools,
  showMajorMinors,
  showGpa,
  showExpectedGraduation,
  showLanguages,
  showYearInSchool,
  showSchoolStanding,
  showPlanToPay,
  showAddress,
  updateLearnerTypeId,
} = useLearnerInformationDisplaySettings(application);

const profileDraft = ref(null);

watch(
  [profile, profileIsLoading],
  ([profileData, isLoading]) => {
    if (!isLoading && profileData && !profileDraft.value) {
      profileDraft.value = structuredClone(toRaw(profileData));
    }
  },
  { immediate: true },
);

const schoolOptions = ref([]);
const lastSelectedSchool = ref(null);
const allLearnerTypes = computed(() => initialOptionsData.value?.learnerTypes);
const learnerType = computed(() => allLearnerTypes.value?.find((each) => each.id === profile.value?.learner_type_id));
const initialCipCodes = computed(() => initialOptionsData.value?.cipCodes);
const languages = computed(() => initialOptionsData.value?.languages);
const learnerInformationSubmissionId = computed(() => learnerInformationSubmission.value?._id);

const countries = computed(() => initialOptionsData.value?.countries);

// Variables
const billingAddressSameAs = ref("");
const isSameAddress = ref(false);
const store = useStore();
const learnerInfoSubmit = ref(null);
const initialCurrentAddress = ref(null);
const initialPermanentAddress = ref(null);
const initialBillingAddress = ref(null);
const submissionError = ref("");
const contracts = computed(() => primaryOrder.value?.billing_rules);
const form = reactive({
  majors: [], // required, at least one
  minors: [],
  cumulativeGpa: null,
  expectedGraduation: null,
  yearInSchool: null,
  paymentPlans: [],
  languagesYouKnow: [],
  hadDisciplinarySanctions: null, // required
  currentAddress: {}, // required
  permanentAddress: {},
  billingAddress: {},
  parentGuardianName: "", // required if student age is under 18 - Could be populated by the 1st parent found
  parentGuardianEmail: "", // required if student age is under 18 - Could be populated by the 1st parent found
  certifyFAFSA: false,
  consideredForScholarships: false,
});
// eslint-disable-next-line max-len
const parentGuardiansUpdate = ref([]); // To avoid modifying the state, we will clone the parents object from profile and modify this one
const schoolDetails = ref(null);
const cipCodes = ref(initialCipCodes.value); // This is equal to Major(s) and Minor(s)
const addressOptions = [
  { label: "Same as current address", value: "currentAddress" },
  { label: "Same as permanent address", value: "permanentAddress" },
  { label: "Other", value: "other" },
];
const displinarySanctionsOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];
const scholarshipOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const addressController = reactive({
  currentAddress: {
    showManualForm: null, // hides autocomplete input then allows to capture address manually (if true)
  },
  permanentAddress: {
    showManualForm: null,
  },
  billingAddress: {
    showManualForm: null,
  },
});

const learnerTypeOptions = computed(() =>
  session.value?.education_levels?.length ? sessionFilteredLearnerTypes.value : allLearnerTypes.value,
);

const formLearnerType = computed({
  get: () => {
    if (!profileDraft.value) return null;
    const learnerTypeId = profileDraft.value.learner_type_id;
    if (!learnerTypeId) return null;
    return learnerTypeOptions.value.find((type) => type.id === learnerTypeId);
  },
  set: (newType) => {
    if (!profileDraft.value) return;
    profileDraft.value.learner_type_id = newType?.id;
    updateLearnerTypeId(newType?.id);

    if (showSchools.value) {
      // Preserve the saved school so a returning student doesn't lose it when toggling learner types.
      schoolOptions.value = schoolDetails.value ? [schoolDetails.value] : [];
    } else {
      lastSelectedSchool.value = null;
      profileDraft.value.colleges = [];
      schoolDetails.value = null;
    }
    if (!showMajorMinors.value) {
      form.majors = [];
      form.minors = [];
    }
  },
});

const expectedGraduationValidation = helpers.regex(/^\d{2}\/\d{4}$/); // regex: Specific format MM/YYYY

const proficiencyValidation = (value) => {
  // Validates that all languages have a proficiency
  return undefined === value.find((language) => !language?.proficiency?.id);
};

const requiredErrorCopy = "This field is required.";
const emailErrorCopy = "Email does not have proper formatting. Please try again.";

const minLengthGpaCopy = "GPA must be from 0.00-4.00.";
const requiredAddressFields = ["address_postal_code", "address_city", "address_state", "address_country_id"];

const futureDateValidation = (value) => {
  if (!hasHomeInstitution.value) {
    return true;
  }
  const regex = /^(0[1-9]|1[0-2])\/\d{4}$/;
  if (!regex.test(value)) {
    return false;
  }
  const [month, year] = value.split("/");
  const selectedDate = new Date(`${year}-${month}-01`);
  const currentDate = new Date();

  return selectedDate > currentDate;
};

const learnerTypeValidation = () => {
  const currentlyInvalid = invalidLearnerTypeFromProfile.value && !currentLearnerTypeIsValid.value;
  const learnerSelected = !!formLearnerType.value?.id;

  return {
    $valid: !(currentlyInvalid || !learnerSelected),
    message: currentlyInvalid ? INVALID_SELECTION : requiredErrorCopy,
  };
};

const schoolValidation = () => {
  const invalidFromProfile = invalidCollegeFromProfile.value;
  const isValidCollege = currentCollegeIsValid.value;

  const currentlyInvalid = invalidFromProfile && !isValidCollege;

  if (!showSchools.value) {
    return { $valid: true };
  }

  return {
    $valid: !(currentlyInvalid || !formSchool.value?.id),
    message: currentlyInvalid ? INVALID_SELECTION : requiredErrorCopy,
  };
};

// Form validation rules (Vuelidate library)
const rules = {
  formLearnerType: {
    validselection: helpers.withMessage(({ $response }) => $response?.message, learnerTypeValidation),
  },
  formSchool: {
    validselection: helpers.withMessage(({ $response }) => $response?.message, schoolValidation),
  },
  majors: {
    required: helpers.withMessage(
      "At least one major is required.",
      requiredIf(() => showMajorMinors.value),
    ),
    maxLength: helpers.withMessage("Select up to two majors", maxLength(2)),
  },
  minors: {
    maxLength: helpers.withMessage("Select up to three minors", maxLength(3)),
  },
  cumulativeGpa: {
    minValue: helpers.withMessage(minLengthGpaCopy, minValue(0)),
    maxValue: helpers.withMessage(minLengthGpaCopy, maxValue(4)),
    required: helpers.withMessage(
      requiredErrorCopy,
      requiredIf(() => showGpa.value),
    ),
  },
  expectedGraduation: {
    valid: helpers.withMessage("Format must be MM/YYYY", expectedGraduationValidation),
    futureDate: helpers.withMessage("Expected graduation date must be in the future", futureDateValidation),
  },
  yearInSchool: {
    required: helpers.withMessage(
      requiredErrorCopy,
      requiredIf(() => showYearInSchool.value),
    ),
  },
  paymentPlans: {},
  hadDisciplinarySanctions: {
    required: helpers.withMessage(
      requiredErrorCopy,
      requiredIf(() => showSchoolStanding.value),
    ),
  },
  currentAddress: {
    address_line_1: {
      required: helpers.withMessage(
        "Address is required",
        requiredIf(() => showAddress.value),
      ),
    },
    address_postal_code: {
      required: helpers.withMessage(
        "The address provided doesn't include a postal code.",
        requiredIf(() => form.currentAddress?.address_line_1?.length),
      ),
    },
    address_city: {
      required: helpers.withMessage(
        "The address provided doesn't include a city.",
        requiredIf(() => form.currentAddress?.address_line_1?.length),
      ),
    },
    address_state: {
      required: helpers.withMessage(
        "The address provided doesn't include a state.",
        requiredIf(() => form.currentAddress?.address_line_1?.length),
      ),
    },
    address_country_id: {
      required: helpers.withMessage(
        "The address provided doesn't include a country.",
        requiredIf(() => form.currentAddress?.address_line_1?.length),
      ),
    },
  },
  permanentAddress: {
    address_line_1: {
      required: helpers.withMessage(
        "Address is required",
        requiredIf(() => showAddress.value),
      ),
    },
    address_postal_code: {
      required: helpers.withMessage(
        "The address provided doesn't include a postal code.",
        requiredIf(() => form.permanentAddress?.address_line_1?.length),
      ),
    },
    address_city: {
      required: helpers.withMessage(
        "The address provided doesn't include a city.",
        requiredIf(() => form.permanentAddress?.address_line_1?.length),
      ),
    },
    address_state: {
      required: helpers.withMessage(
        "The address provided doesn't include a state.",
        requiredIf(() => form.permanentAddress?.address_line_1?.length),
      ),
    },
    address_country_id: {
      required: helpers.withMessage(
        "The address provided doesn't include a country.",
        requiredIf(() => form.permanentAddress?.address_line_1?.length),
      ),
    },
  },
  billingAddress: {
    address_line_1: {
      required: helpers.withMessage(
        "Address is required",
        requiredIf(() => contractNumber.value !== ""),
      ),
    },
    address_postal_code: {
      required: helpers.withMessage(
        "The address provided doesn't include a postal code.",
        requiredIf(() => form.billingAddress?.address_line_1?.length && contractNumber.value !== ""),
      ),
    },
    address_city: {
      required: helpers.withMessage(
        "The address provided doesn't include a city.",
        requiredIf(() => form.billingAddress?.address_line_1?.length && contractNumber.value !== ""),
      ),
    },
    address_state: {
      required: helpers.withMessage(
        "The address provided doesn't include a state.",
        requiredIf(() => form.billingAddress?.address_line_1?.length && contractNumber.value !== ""),
      ),
    },
    address_country_id: {
      required: helpers.withMessage(
        "The address provided doesn't include a country.",
        requiredIf(() => form.billingAddress?.address_line_1?.length && contractNumber.value !== ""),
      ),
    },
  },
  languagesYouKnow: {
    required: helpers.withMessage("At least one language is required.", requiredIf(showLanguages.value)),
    missingProficiency: helpers.withMessage("Set a proficiency for each language", proficiencyValidation),
  },
  parentGuardianName: {
    required: helpers.withMessage(
      requiredErrorCopy,
      requiredIf(() => isUnderage.value),
    ),
    maxLength: helpers.withMessage(
      ({ $params }) => `Name must have fewer than ${$params.max} characters.`,
      maxLength(100),
    ),
  },
  parentGuardianEmail: {
    required: helpers.withMessage(
      requiredErrorCopy,
      requiredIf(() => isUnderage.value),
    ),
    validEmailAddress: helpers.withMessage(emailErrorCopy, validEmailAddress),
    email: helpers.withMessage(emailErrorCopy, email),
  },
};
const v$ = useVuelidate(rules, form);

const filteredProficiencyLevelsOptions = computed(() => {
  return proficiencyLevelsOptions.filter((option) => option.value !== "All Levels");
});

const hideBillingAddressInput = computed(() => {
  return ["currentAddress", "permanentAddress", ""].includes(billingAddressSameAs.value);
});

const currentUser = computed(() => {
  return store.state.currentUser;
});
const oktaId = computed(() => {
  return currentUser.value?.participantId ?? "";
});

const languagesYouKnowFormatted = computed(() => {
  // Helpful for profile service that only stores the ids
  return form.languagesYouKnow.map((language) => {
    return {
      language_id: language?.id ?? "",
      language_proficiency_id: language?.proficiency?.id ?? "",
    };
  });
});

const majorsFormatted = computed(() => {
  // Helpful for profile service that only stores the id/cip_code
  return form.majors.map((major) => {
    return { cip_code: major.cip_code };
  });
});

const minorsFormatted = computed(() => {
  return form.minors.map((minor) => {
    return { cip_code: minor.cip_code };
  });
});

const paymentPlansFormatted = computed(() => {
  // Get their values instead of labels
  return form.paymentPlans.map((plan) => plan.value);
});

const disciplinarySanctionsFormatted = computed(() => {
  return form.hadDisciplinarySanctions?.value === "yes";
});

const formatGPA = computed(() => {
  const parsedGPA = Number.parseFloat(form.cumulativeGpa);
  if (isNaN(parsedGPA)) return null;
  return parsedGPA % 1 !== 0 ? Number.parseFloat(parsedGPA.toFixed(2)) : Number.parseFloat(parsedGPA.toFixed(1));
});

const profileSaveObject = computed(() => {
  if (!profileDraft.value) return null;

  return {
    ...profileDraft.value,
    majors: majorsFormatted.value,
    minors: minorsFormatted.value,
    cumulative_gpa: formatGPA.value,
    expected_graduation_date: form.expectedGraduation,
    languages: languagesYouKnowFormatted.value,
    year_in_school_at_time_of_api_program: form.yearInSchool?.value ?? "",
    plans_for_paying: paymentPlansFormatted.value,
    manual_current_address_completion: addressController.currentAddress.showManualForm,
    current_address_country_id: form.currentAddress?.address_country_id ?? null,
    current_address_city: form.currentAddress?.address_city ?? null,
    current_address_line_1: form.currentAddress?.address_line_1 ?? null,
    current_address_line_2: form.currentAddress?.address_line_2 ?? null,
    current_address_state: form.currentAddress?.address_state ?? null,
    current_address_postal_code: form.currentAddress?.address_postal_code ?? null,
    current_address_neighborhood_name: form.currentAddress?.neighborhood ?? null,
    current_address_administrative_area_level_2: form.currentAddress?.administrative_area_level_2 ?? null,
    manual_permanent_address_completion: addressController.permanentAddress.showManualForm,
    permanent_address_country_id: form.permanentAddress?.address_country_id ?? null,
    permanent_address_city: form.permanentAddress?.address_city ?? null,
    permanent_address_line_1: form.permanentAddress?.address_line_1 ?? null,
    permanent_address_line_2: form.permanentAddress?.address_line_2 ?? null,
    permanent_address_postal_code: form.permanentAddress?.address_postal_code ?? null,
    permanent_address_state: form.permanentAddress?.address_state ?? null,
    permanent_address_neighborhood_name: form.permanentAddress?.neighborhood ?? null,
    permanent_address_administrative_area_level_2: form.permanentAddress?.administrative_area_level_2 ?? null,
    manual_billing_address_completion: addressController.billingAddress.showManualForm,
    billing_address_country_id: form.billingAddress?.address_country_id ?? null,
    billing_address_city: form.billingAddress?.address_city ?? null,
    billing_address_line_1: form.billingAddress?.address_line_1 ?? null,
    billing_address_line_2: form.billingAddress?.address_line_2 ?? null,
    billing_address_state: form.billingAddress?.address_state ?? null,
    billing_address_postal_code: form.billingAddress?.address_postal_code ?? null,
    billing_address_neighborhood_name: form.billingAddress?.address_neighborhood ?? null,
    billing_address_administrative_area_level_2: form.billingAddress?.administrative_area_level_2 ?? null,
    parents: parentGuardiansUpdate.value,
    disciplinary_sanctions: disciplinarySanctionsFormatted.value,
    user_types: profileDraft.value?.user_types ?? [],
    okta_id: profileDraft.value?.okta_id,
  };
});

const formioSaveObject = computed(() => {
  // Data that goes to formio at first submission
  return {
    data: {
      application_id: applicationId.value,
      "school-name": hasHomeInstitution.value
        ? {
            label: schoolDetails.value?.name ?? "",
            value: schoolDetails.value?.id?.toString().padStart(8, "0") ?? "",
          }
        : {},
      major: {
        name: form.majors[0]?.cip_title ?? "",
      }, // First major
      minor: form.minors[0]?.cip_title !== undefined ? { name: form.minors[0].cip_title } : {}, // First minor
      gpa: formatGPA.value,
      "academic-level": form.yearInSchool?.value ?? "", // year in school
      languageProficiency: _.camelCase(form.languagesYouKnow?.[0]?.proficiency?.value ?? ""), // of first lenguage selected, also https://lodash.com/docs/4.17.15#camelCase
      disciplinarySanctions: form.hadDisciplinarySanctions?.value,
      learnerType: learnerType?.value?.value,
    },
  };
});

const formioPatchObject = computed(() => {
  // Data that goes to formio when updating submission
  // Re-uses previous computed property to avoid duplicating logic to fetch values
  // Updates studentApplicationStage2Step1
  return [
    patchFormioObject("application_id", formioSaveObject.value.data.application_id),
    patchFormioObject("school-name", formioSaveObject.value.data["school-name"]),
    patchFormioObject("major", formioSaveObject.value.data.major),
    patchFormioObject("minor", formioSaveObject.value.data.minor),
    patchFormioObject("gpa", formioSaveObject.value.data.gpa),
    patchFormioObject("academic-level", formioSaveObject.value.data["academic-level"]),
    patchFormioObject("languageProficiency", formioSaveObject.value.data.languageProficiency),
    patchFormioObject("disciplinarySanctions", formioSaveObject.value.data.disciplinarySanctions),
  ];
});

const contractNumber = computed(() => {
  const appliableContract = billingAgreement(contracts.value, session.value, {
    billingContractId: primaryOrder.value?.billing_contract_id,
  });
  return appliableContract?.contract_number ?? "";
});

const isUnderage = computed(() => {
  // Bail if birthdate not set
  if (!currentUser.value?.birthdate) return false;

  const today = new Date();
  const eighteenYearsAgo = subYears(today, 18);
  return isAfter(new Date(currentUser.value.birthdate), eighteenYearsAgo);
});

const isFacultyLedProgram = computed(() =>
  session.value?.session_types?.some((val) => val.id === sessionType.faculty_led),
);

const hasFinancialAid = computed(() => {
  return form.paymentPlans.find((plan) => compareStringsInLowerCase(plan.value, "financialAid")) !== undefined;
});

const hasSelectedPaymentPlan = () =>
  Boolean(
    form.paymentPlans.find(
      (plan) =>
        compareStringsInLowerCase(plan.value, "financialAid") ||
        compareStringsInLowerCase(plan.value, "scholarshipsGrantsFromYourSchool"),
    ),
  );

const showScholarshipQuestion = computed(() => {
  if (!isUsCollegeStudent.value) return false;
  if (!hasScholarshipElegiblityEnabled?.value) return false;
  if (!hasSelectedPaymentPlan()) return false;

  const isBasePriceOver2000 = session.value.base_price_in_cents > OVER_PRICE_2000;
  if (!isBasePriceOver2000) return false;
  if (!schoolDetails?.value?.api_relationship) return false;
  if (!compareStringsInLowerCase(schoolDetails.value?.api_relationship ?? "", API_CONNECT_MEMBER)) return false;
  if (isFacultyLedProgram.value) return false;

  return true;
});

const searchSchools = async ({ search, loading }) => {
  if (search.length === 0) {
    // Reset to just the currently-selected school so it stays selectable without an extra fetch.
    schoolOptions.value = schoolDetails.value ? [schoolDetails.value] : [];
    return;
  }
  loading(true);
  const universityParams = {
    account_types: ["7bda5a4e-23fe-45eb-b344-5c5bfd8a5164"],
    field: "name",
    order: "ASC",
  };
  entitiesService
    .getEntities({ q: search, extraParams: universityParams })
    .then(({ data }) => {
      const results = data?.data?.items ?? [];
      // Keep the currently-selected school in the options so it stays displayed until the user persists a new one.
      const selected = schoolDetails.value;
      schoolOptions.value =
        selected && !results.some((school) => school.id === selected.id) ? [selected, ...results] : results;
    })
    .catch(() => {
      schoolOptions.value = schoolDetails.value ? [schoolDetails.value] : [];
    })
    .finally(() => {
      loading(false);
    });
};

const updateSchoolDetails = async (schoolId) => {
  if (schoolId) {
    const school = await entitiesService.getEntityById(schoolId);
    schoolDetails.value = school.data.data.items;
  } else {
    schoolDetails.value = null;
  }
};

const formSchool = computed({
  get: () => {
    if (!profileDraft.value) return null;
    if (!hasHomeInstitution.value) return null;
    const collegeId = profileDraft.value.colleges?.[0]?.college_id;
    if (!collegeId) return null;

    return composableUniversities.value.find((uni) => uni.id === collegeId) ?? lastSelectedSchool.value ?? null;
  },
  set: async (newSchool) => {
    if (!profileDraft.value) return;

    if (newSchool?.id) {
      lastSelectedSchool.value = newSchool;
      if (!profileDraft.value.colleges) profileDraft.value.colleges = [];
      profileDraft.value.colleges[0] = { college_id: newSchool.id };
      await updateSchoolDetails(newSchool.id);
    } else {
      lastSelectedSchool.value = null;
      profileDraft.value.colleges = [];
      await updateSchoolDetails(null);
    }
  },
});

const {
  filteredLearnerTypes: sessionFilteredLearnerTypes,
  currentLearnerTypeIsValid,
  invalidLearnerTypeFromProfile,
} = useLearnerTypes(
  profile,
  session,
  computed(() => profileDraft.value?.learner_type_id),
);

const {
  universities: composableUniversities,
  currentCollegeIsValid,
  invalidCollegeFromProfile,
} = useAllowedUniversities(profile, session, schoolOptions, schoolDetails);

watch(
  [
    () => v$.value.currentAddress.$errors,
    () => v$.value.permanentAddress.$errors,
    () => v$.value.billingAddress.$errors,
  ],
  (
    // Renders form to manually set address if autocomplete caused an error
    [newCurrentAddress, newPermanentAddress, newBillingAddress],
  ) => {
    // Once flag is active we won't show back the autocomplete
    if (
      newCurrentAddress?.some((error) => {
        return requiredAddressFields.includes(error.$property);
      })
    ) {
      addressController.currentAddress.showManualForm = true;
    }

    if (
      newPermanentAddress?.some((error) => {
        return requiredAddressFields.includes(error.$property);
      })
    ) {
      addressController.permanentAddress.showManualForm = true;
    }

    if (
      newBillingAddress?.some((error) => {
        return requiredAddressFields.includes(error.$property);
      })
    ) {
      addressController.billingAddress.showManualForm = true;
    }
  },
);

// Permanent address
watch(isSameAddress, (newValue) => {
  if (newValue) {
    form.permanentAddress = { ...form.currentAddress };
    if (billingAddressSameAs.value === "permanentAddress") {
      // Copy to billing if available
      form.billingAddress = { ...form.permanentAddress };
    }

    if (addressController.currentAddress.showManualForm) {
      // stores if it was copied from autocomplete or manual input
      addressController.permanentAddress.showManualForm = true;
    }
  } else {
    initialPermanentAddress.value = null; // Clears out permanent address autocomplete input
    form.permanentAddress = {};
    addressController.permanentAddress.showManualForm = null;
  }
});

watch(billingAddressSameAs, (newValue, oldValue) => {
  if (newValue === "currentAddress") {
    // Copy current address
    form.billingAddress = { ...form.currentAddress };
  }
  if (newValue === "permanentAddress") {
    // Copy permanent address
    form.billingAddress = { ...form.permanentAddress };
  }
  if (newValue === "other" && oldValue !== "") {
    // adding oldValue prevents from removing billing address when user re-visits the form
    // he should see the previous billing address he set
    initialBillingAddress.value = {}; // Clears out billing address input
    form.billingAddress = {}; // Let the user input an address
  }
});

watch(
  () => [form.parentGuardianEmail, form.parentGuardianName],
  ([newEmail, newName]) => {
    // When setting either the email or name of a parent guardian we will set/update the first one in their relationship
    if (parentGuardiansUpdate.value[0] !== undefined) {
      parentGuardiansUpdate.value[0].name = newName;
      parentGuardiansUpdate.value[0].email = newEmail;
      return;
    }
    parentGuardiansUpdate.value.push({ name: newName, email: newEmail });
  },
);

watch(
  () => form.languagesYouKnow,
  (newLanguages) => {
    // To each language selected we add a 'proficiency' property to capture their id
    // This avoids infinite loop: Checks if an item does not have the proficiency property
    if (!newLanguages.find((language) => !Object.hasOwn(language, "proficiency"))) {
      return;
    }

    // Add proficiency property if it does not have it
    form.languagesYouKnow = newLanguages.map((language) => {
      if (!Object.hasOwn(language, "proficiency")) {
        language["proficiency"] = null;
        v$._value.languagesYouKnow.$reset();
      }
      return language;
    });
  },
  { deep: true },
);

watch(
  form.currentAddress,
  () => {
    // Keep addresses up to date if current address changes
    if (isSameAddress.value) form.permanentAddress = { ...form.currentAddress };
    if (billingAddressSameAs.value === "currentAddress") form.billingAddress = { ...form.currentAddress };
  },
  { deep: true },
);

watch(
  form.permanentAddress,
  () => {
    // Keep address up to date if permanent address changes
    if (billingAddressSameAs.value === "permanentAddress") form.billingAddress = { ...form.permanentAddress };
  },
  { deep: true },
);

watch(
  () => form.paymentPlans,
  () => {
    if (!hasFinancialAid.value) {
      // reset additional hidden fields
      form.certifyFAFSA = false;
      form.consideredForScholarships = "no";
    }
  },
);

const updateAddress = (addressType, addressInfo) => {
  // Address type should be either currentAddress or permanentAddress
  // So it updates the right object inside 'form' reactive object
  const {
    street_address_1,
    street_address_2,
    number,
    city,
    state,
    postal_code,
    lat,
    long,
    iso, // country id
    country,
    utc_offset_minutes,
    neighborhood,
    administrative_area_level_2,
  } = addressInfo;
  form[addressType]["address_line_1"] = `${number ? `${number} ` : ""}${street_address_1 || ""}`;
  form[addressType]["address_line_2"] = street_address_2;
  form[addressType]["address_postal_code"] = postal_code || null;
  form[addressType]["address_city"] = city;
  form[addressType]["address_state"] = state;
  form[addressType]["neighborhood"] = neighborhood || null;
  form[addressType]["address_lat_lng"] = `${lat || ""} ${long || ""}`; //37.4224428 -122.0829089197085
  form[addressType]["address_country_id"] = iso || null;
  form[addressType]["timezone_offset_mins"] = utc_offset_minutes;
  form[addressType]["country"] = country || ""; // label
  form[addressType]["administrative_area_level_2"] = administrative_area_level_2 || "";
};

const updateAddressManually = (addressType, addressInfo) => {
  form[addressType]["address_line_1"] = addressInfo["address"] || "";
  form[addressType]["address_postal_code"] = addressInfo["address_postal_code"] || null;
  form[addressType]["address_city"] = addressInfo["address_city"];
  form[addressType]["address_state"] = addressInfo["address_state"];
  form[addressType]["address_country_id"] = addressInfo["address_country_id"] || null;
  form[addressType]["country"] = addressInfo["country"] || ""; // label of country
  form[addressType]["administrative_area_level_2"] =
    addressInfo["administrative_area_level_2"] || form[addressType]["administrative_area_level_2"];

  // We do not capture these manually, clear them
  form[addressType]["neighborhood"] = null;
  form[addressType]["address_lat_lng"] = null;
};

// It will inject properties in icon and get string of the html generated
const getComponentHtmlWithProps = (component, props = {}) => {
  const tempDiv = document.createElement("div");
  const app = createApp({
    render: () => h(component, props),
  });
  app.mount(tempDiv);
  return tempDiv.innerHTML;
};

const helpIconHtmlAsString = getComponentHtmlWithProps(HelpIcon, {
  cssClass: "w-5 h-5 flex-shrink-0",
});

const tooltipContent = computed(() => {
  return `
    <div class="flex items-center space-x-2">
      ${helpIconHtmlAsString}
      <span>We accept GPAs based on a 4.0 scale. If you institution uses a different scale, please do your best to convert your GPA. We’ll contact you if needed after reviewing your transcript.</span>
    </div>
  `;
});

const searchCipCodes = async ({ search, loading }) => {
  // Majors and minors will come from Learning Service from now own. (V3)
  try {
    if (search === "") {
      cipCodes.value = [...initialCipCodes.value];
      return;
    }
    const searchResults = await cipCodesService.list(search, "cip_title", "ASC", 25);
    const filteredInitialResults = initialCipCodes.value.filter((cip) => cip.cip_title.indexOf(search) !== -1);
    const resultSet = [...filteredInitialResults, ...searchResults];
    cipCodes.value = resultSet.filter(
      (cipResult, index, self) => index === self.findIndex((c) => c.cip_code === cipResult.cip_code),
    );
  } catch (error) {
    cipCodes.value = [];
  } finally {
    loading(false);
  }
};

const submitLearnerInfo = async () => {
  learnerInfoSubmit.value.startLoading();
  try {
    const financialAidPayload = {
      profile_id: oktaId.value,
      application_id: applicationId.value,
      using_financial_aid: hasSelectedPaymentPlan(),
      certified_fafsa_eligible: form.consideredForScholarships === "no" ? false : form.certifyFAFSA,
      considered_for_scholarships: form.consideredForScholarships === "yes",
    };
    if (hasSelectedPaymentPlan()) {
      if (financialAid.value) {
        await updateFinancialAid(financialAidPayload);
      } else {
        await createFinancialAid(financialAidPayload);
      }
    } else if (financialAid.value) {
      // Used to be selected
      await updateFinancialAid(financialAidPayload);
    }

    // Update Profile Service
    await updateProfile(oktaId.value, profileSaveObject.value);

    await application.value.updateProfileColleges(formLearnerType.value?.id, formSchool.value);

    // Create/Update formio submission
    let formio_response;
    if (!learnerInformationSubmissionId.value) {
      // Create
      formio_response = await saveLearnerInformationSubmission(formioSaveObject.value);
    } else {
      // Update - PATCH: We won't use PUT because it would replace all data and this form has other forms inside of it, PATCH avoids edge-cases
      formio_response = await partialUpdateApplicationSubmission(
        "studentapplicationstage2step1",
        learnerInformationSubmissionId.value,
        formioPatchObject.value,
      );
    }

    if (profile && formio_response?._id !== undefined) {
      props.goToNextStep();
    }
  } catch (e) {
    submissionError.value = e;
  } finally {
    learnerInfoSubmit.value.stopLoading();
  }
};

const prepareInitialAddress = (addressType = "") => {
  // Address component needs a specific object in order to populate what users saved before
  // We'll have to query for the country to get its name
  switch (addressType) {
    case "current": {
      initialCurrentAddress.value = {
        street_address_1: profile.value?.current_address_line_1 ?? null,
        street_address_2: profile.value?.current_address_line_2 ?? null,
        city: profile.value?.current_address_city ?? null,
        state: profile.value?.current_address_state ?? null,
        country: countries.value?.find((each) => each.id === profile.value?.address_country_id)?.name ?? null,
        postal_code: profile.value?.current_address_postal_code ?? null,
        utc_offset_minutes: null,
        administrative_area_level_2: profile?.value?.administrative_area_level_2 ?? null,
      };
      break;
    }
    case "permanent": {
      initialPermanentAddress.value = {
        street_address_1: profile.value?.permanent_address_line_1,
        street_address_2: profile.value?.permanent_address_line_2,
        city: profile.value?.permanent_address_city,
        state: profile.value?.permanent_address_state,
        country:
          countries.value?.find((each) => each.id === profile.value?.permanent_address_country_id)?.name ?? null,
        postal_code: profile.value?.permanent_address_postal_code,
        utc_offset_minutes: null,
        administrative_area_level_2: profile?.value?.administrative_area_level_2 ?? null,
      };
      break;
    }
    case "billing": {
      initialBillingAddress.value = {
        street_address_1: profile.value?.billing_address_line_1,
        street_address_2: profile.value?.billing_address_line_2,
        city: profile.value?.billing_address_city,
        state: profile.value?.billing_address_state,
        country: countries.value?.find((each) => each.id === profile.value?.billing_address_country_id)?.name ?? null,
        postal_code: profile.value?.billing_address_postal_code,
        utc_offset_minutes: null,
        administrative_area_level_2: profile?.value?.administrative_area_level_2 ?? null,
      };
      break;
    }
  }
};

function areAddressesSame(address1, address2) {
  // List of fields we want to compare
  const fieldsToCompare = [
    "address_country_id",
    "address_line_1",
    "address_line_2",
    "address_postal_code",
    "address_city",
    "address_state",
    "address_neighborhood",
    "administrative_area_level_2",
  ];

  const allFieldsNull = (address) => fieldsToCompare.every((field) => address[field] === null);

  // If both addresses have all fields set to null, return false
  if (allFieldsNull(address1) && allFieldsNull(address2)) {
    return false;
  }

  // ensure they are equal and not null
  return fieldsToCompare.every((field) => address1[field] === address2[field]);
}

const populateFormCipCodes = async (formElementName, profileCipCodeList) => {
  const missingCipCodes = profileCipCodeList.map((cipCodeObj) => cipCodeObj.cip_code);
  if (missingCipCodes.length > 0) {
    form[formElementName] = cipCodes.value.filter((cipCodeObj) => {
      const isFound = profileCipCodeList.map((each) => Number(each.cip_code)).includes(Number(cipCodeObj.cip_code));
      if (isFound) {
        const codeIndex = missingCipCodes.findIndex((code) => code === cipCodeObj.cip_code);
        if (codeIndex >= 0) {
          missingCipCodes.splice(codeIndex, 1);
        }
      }
      return isFound;
    });
  }
  // Load the missing codes
  if (missingCipCodes.length > 0) {
    missingCipCodes.forEach(async (missingCode) => {
      const lookedUpCode = await cipCodesService.getOne(missingCode);
      form[formElementName] = [...form[formElementName], lookedUpCode];
    });
  }
};

const getProfileData = async () => {
  if (!_.isEmpty(profile.value)) {
    if (profile.value?.learner_type_id && invalidLearnerTypeFromProfile.value) {
      v$.value.formLearnerType.$touch();
    }

    if (hasHomeInstitution.value && profile.value?.colleges[0] !== undefined && invalidCollegeFromProfile.value) {
      v$.value.formSchool.$touch();
    }

    // Majors
    await populateFormCipCodes("majors", profile.value.majors);

    // Minors
    await populateFormCipCodes("minors", profile.value.minors);

    // Cumulative GPA
    form.cumulativeGpa =
      typeof profile.value?.cumulative_gpa === "number" ? profile.value.cumulative_gpa.toString() : null;

    // Expected graduation date
    form.expectedGraduation = profile.value?.expected_graduation_date ?? null;

    // Languages + Proficiency
    // I know it's ugly, but it works and i was under the clock!
    for (let i = 0; i < profile.value?.languages.length ?? []; i++) {
      const foundLanguage = languages.value.findIndex(
        (language) => language.id === profile.value.languages[i].language_id,
      );

      if (foundLanguage > -1) {
        // Only set proficiency if language was found
        // Push returns the latest length of the array, we'll use that to get the latest index
        let latest = form.languagesYouKnow.push(JSON.parse(JSON.stringify(languages.value[foundLanguage])));

        latest = latest - 1; // Get index

        const foundProficiency = proficiencyLevelsOptions.findIndex(
          (proficiencyLevel) => proficiencyLevel.id === profile.value.languages[i].language_proficiency_id,
        );
        form.languagesYouKnow[latest]["proficiency"] =
          foundProficiency > -1 ? JSON.parse(JSON.stringify(proficiencyLevelsOptions[foundProficiency])) : {};
      }
    }

    // Year in school
    const academicYear = profile.value?.year_in_school_at_time_of_api_program ?? null;
    form.yearInSchool = !academicYear
      ? null
      : JSON.parse(JSON.stringify(ACADEMIC_YEARS.find((year) => year.value === academicYear)));
    // Plans for paying
    if (Array.isArray(profile.value?.plans_for_paying)) {
      for (let i = 0; i < profile.value.plans_for_paying.length; i++) {
        const foundPlan = PAYMENT_PLANS.find((plan) => plan.value === profile.value.plans_for_paying[i]);
        if (foundPlan !== undefined) form.paymentPlans.push(JSON.parse(JSON.stringify(foundPlan)));
      }
    }
    form.consideredForScholarships = financialAid?.value?.considered_for_scholarships ? "yes" : "no";
    form.certifyFAFSA = financialAid?.value?.certified_fafsa_eligible ?? false;

    // School standing - hadDisciplinarySanctions
    const sanctionToFind =
      profile.value?.disciplinary_sanctions === true
        ? "yes"
        : profile.value?.disciplinary_sanctions === false
          ? "no"
          : null;
    const foundSanction = displinarySanctionsOptions.find((option) => option.value === sanctionToFind);
    form.hadDisciplinarySanctions = foundSanction ? JSON.parse(JSON.stringify(foundSanction)) : null;

    // Current address - AddressComponent does not trigger an update event when the initialAddressInfo prop is set
    // so we need to set the data in the form too
    form.currentAddress.country =
      countries.value?.find((each) => each.id === profile.value?.address_country_id)?.name ?? "";
    form.currentAddress.address_country_id = profile.value?.current_address_country_id ?? null;
    form.currentAddress.address_line_1 = profile.value?.current_address_line_1 ?? null;
    form.currentAddress.address_line_2 = profile.value?.current_address_line_2 ?? null;
    form.currentAddress.address_postal_code = profile.value?.current_address_postal_code ?? null;
    form.currentAddress.address_city = profile.value?.current_address_city ?? null;
    form.currentAddress.address_state = profile.value?.current_address_state ?? null;
    form.currentAddress.address_neighborhood = profile.value?.current_address_neighborhood_name ?? null;
    form.currentAddress.administrative_area_level_2 =
      profile.value.current_address_administrative_area_level_2 ?? null;

    addressController.currentAddress.showManualForm = profile.value?.manual_current_address_completion || null;
    // Current address - This will populate an object that will be used in initialAddressInfo prop to populate the input
    prepareInitialAddress("current");

    // Permanent address
    form.permanentAddress.country =
      countries.value?.find((each) => each.id === profile.value?.permanent_address_country_id)?.name ?? "";
    form.permanentAddress.address_country_id = profile.value?.permanent_address_country_id ?? null;
    form.permanentAddress.address_line_1 = profile.value?.permanent_address_line_1 ?? null;
    form.permanentAddress.address_line_2 = profile.value?.permanent_address_line_2 ?? null;
    form.permanentAddress.address_postal_code = profile.value?.permanent_address_postal_code ?? null;
    form.permanentAddress.address_city = profile.value?.permanent_address_city ?? null;
    form.permanentAddress.address_state = profile.value?.permanent_address_state ?? null;
    form.permanentAddress.address_neighborhood = profile.value?.permanent_address_neighborhood_name ?? null;
    form.permanentAddress.administrative_area_level_2 =
      profile.value.permanent_address_administrative_area_level_2 || null;
    addressController.permanentAddress.showManualForm = profile.value?.manual_permanent_address_completion ?? null;
    prepareInitialAddress("permanent");

    // Billing address
    form.billingAddress.country =
      countries.value?.find((each) => each.id === profile.value?.billing_address_country_id)?.name ?? "";
    form.billingAddress.address_country_id = profile.value?.billing_address_country_id ?? null;
    form.billingAddress.address_line_1 = profile.value?.billing_address_line_1 ?? null;
    form.billingAddress.address_line_2 = profile.value?.billing_address_line_2 ?? null;
    form.billingAddress.address_postal_code = profile.value?.billing_address_postal_code ?? null;
    form.billingAddress.address_city = profile.value?.billing_address_city ?? null;
    form.billingAddress.address_state = profile.value?.billing_address_state ?? null;
    form.billingAddress.address_neighborhood = profile.value?.billing_address_neighborhood_name ?? null;
    form.billingAddress.administrative_area_level_2 =
      profile.value?.billing_address_administrative_area_level_2 || null;
    form.billingAddress.administrative_area_level_2 =
      profile.value.billing_address_administrative_area_level_2 || null;
    addressController.billingAddress.showManualForm = profile.value?.manual_billing_address_completion ?? null;
    prepareInitialAddress("billing");

    // Check if addresses are the same, if so, check the "same address" checkbox
    if (areAddressesSame(form.currentAddress, form.permanentAddress)) {
      isSameAddress.value = true; // Check the "same address" checkbox and hide the current address field
    }

    // Check which billing address matches current or permanent address, if so, we mark the corresponding radio-button
    if (areAddressesSame(form.billingAddress, form.currentAddress)) {
      billingAddressSameAs.value = "currentAddress";
    } else if (areAddressesSame(form.billingAddress, form.permanentAddress)) {
      billingAddressSameAs.value = "permanentAddress";
    } else if (
      Object.keys(form.billingAddress).some(
        (billingAddressProperty) => typeof form.billingAddress[billingAddressProperty] === "string",
      )
    ) {
      // If any of the properties from billing address is not a null value then we set radio to 'Other'
      billingAddressSameAs.value = "other";
    }

    // Parent Guardian - This form will allow us to view/edit the first parent
    // We clone the entire list of parents but a watcher is in place to modify the first one
    parentGuardiansUpdate.value = JSON.parse(JSON.stringify(profile.value.parents));

    if (profile.value?.parents[0] !== undefined) {
      form.parentGuardianEmail = profile.value.parents[0].email;
      form.parentGuardianName = profile.value.parents[0].name;
    }
  }
};

const preSelectAddress = (addressType) => {
  // Address type is either; currentAddress, permanentAddress, other
  billingAddressSameAs.value = addressType;
};

// Lifecycle hooks
onBeforeMount(async () => {
  // Pre-populate
  await getProfileData();
  // load users college
  if (profile.value && profile.value.colleges?.length > 0) {
    const college_id = profile.value.colleges[0].college_id;
    await updateSchoolDetails(college_id);
    // Seed search options with the saved school so formSchool's lookup resolves on initial render.
    if (schoolDetails.value) {
      schoolOptions.value = [schoolDetails.value];
    }
  }
});
</script>
<template>
  <div
    class="text-indigo-base text-left bg-white w-full px-4 pb-4 border-collapse md:mt-0 md:p-0"
    data-testid="learner-information-form"
  >
    <div class="grid grid-cols-1 gap-6 md:gap-7 md:pt-4">
      <div>
        <h1 class="font-bold text-[28px] uppercase">
          Learner Information
        </h1>
      </div>
      <p class="font-medium text-xs">
        Fields marked with (*) are required.
      </p>
      <!-- Learner Type -->
      <SingleSelect
        v-if="showLearnerTypes"
        id="learnerType"
        testId="learner-type-form-input"
        v-model.trim="formLearnerType"
        label="What best describes you?*"
        :vuelidate-field="v$.formLearnerType"
        :options="learnerTypeOptions"
        option-label="value"
      >
        <template v-if="formLearnerType?.id === GAP_YEAR_STUDENT" #help-content>
          <p class="text-sm">
            For students taking a break between high school and college.
          </p>
        </template>
      </SingleSelect>
      <!-- Current School -->
      <SchoolSearchSelect
        v-if="showSchools"
        id="school"
        v-model="formSchool"
        test-id="school-form-input"
        label="Current School*"
        placeholder="Type to search your university..."
        description='Start typing your school name to search — e.g. type "Florida" for University of Central Florida.'
        :vuelidate-field="v$.formSchool"
        :options="composableUniversities"
        @search="searchSchools"
      />
      <!-- Major(s) -->
      <SingleSelect
        v-if="showMajorMinors"
        id="majors"
        v-model.trim="form.majors"
        data-testid="majors"
        label="Major(s)*"
        :vuelidate-field="v$.majors"
        :options="cipCodes"
        :search-method="true"
        :allow-multiple-options="true"
        option-label="cip_title"
        @search="searchCipCodes"
      />

      <!-- Minor(s) -->
      <SingleSelect
        v-if="showMajorMinors"
        id="minors"
        v-model.trim="form.minors"
        data-testid="minors"
        label="Minor(s)"
        :vuelidate-field="v$.minors"
        :options="cipCodes"
        :search-method="true"
        :allow-multiple-options="true"
        option-label="cip_title"
        @search="searchCipCodes"
      />
      <!-- 2 cols -->
      <div v-if="showGpa || showExpectedGraduation" class="grid grid-cols-2 gap-y-7 gap-x-7">
        <div v-if="showGpa">
          <!-- Cumulative GPA -->
          <TextField
            id="gpa"
            v-model.trim="form.cumulativeGpa"
            data-testid="gpa"
            label="Cumulative GPA*"
            placeholder="Enter Your GPA"
            :vuelidate-field="v$.cumulativeGpa"
            type="number"
            label-class="!justify-start"
            :show-errors="true"
          >
            <template #label-icon>
              <Tooltip class="pl-2" :is-html="true" :tooltip="tooltipContent" />
            </template>
          </TextField>
        </div>
        <div v-if="showExpectedGraduation">
          <!-- Expected Graduation -->
          <TextField
            id="graduationDate"
            v-model.trim="form.expectedGraduation"
            data-testid="graduationDate"
            label="Expected Graduation*"
            placeholder="MM/YYYY"
            :vuelidate-field="v$.expectedGraduation"
            :show-errors="true"
            :input-mask="{ maskType: 'month-year' }"
          />
        </div>
      </div>

      <!-- Languages you know -->
      <SingleSelect
        v-if="showLanguages"
        id="languagesYouKnow"
        v-model.trim="form.languagesYouKnow"
        data-testid="languagesYouKnow"
        label="What languages do you speak?*"
        :vuelidate-field="v$.languagesYouKnow"
        :options="languages"
        :allow-multiple-options="true"
        option-label="value"
      />

      <!-- Language proficiency (1 per lang selected)-->
      <div v-if="showLanguages && form.languagesYouKnow.length">
        <div class="grid grid-cols-2 gap-y-7 gap-x-7">
          <span class="font-medium text-sm">Language name</span>
          <span class="font-medium text-sm md:whitespace-nowrap">What is your proficiency in each language?*</span>
        </div>
        <div
          v-for="(language, index) in form.languagesYouKnow"
          :key="index"
          class="grid grid-cols-2 items-center gap-6 md:gap-7"
        >
          <span>{{ language.value }}</span>
          <SingleSelect
            :id="'proficiency-' + index"
            v-model.trim="language.proficiency"
            :data-testid="'proficiency-' + index"
            :vuelidate-field="v$.languagesYouKnow"
            :options="filteredProficiencyLevelsOptions"
            option-label="value"
          />
        </div>
      </div>

      <!-- Year in school -->
      <SingleSelect
        v-if="showYearInSchool"
        id="yearSchool"
        v-model.trim="form.yearInSchool"
        data-testid="yearSchool"
        label="Year in school at time of program.*"
        :vuelidate-field="v$.yearInSchool"
        :options="ACADEMIC_YEARS.filter(academicYear => academicYear.visible)"
      />

      <!-- Payment Plans-->
      <SingleSelect
        v-if="showPlanToPay"
        id="paymentplans"
        v-model.trim="form.paymentPlans"
        data-testid="paymentplans"
        label="How do you plan to pay for your program?"
        :vuelidate-field="v$.paymentPlans"
        :options="PAYMENT_PLANS"
        :allow-multiple-options="true"
      />

      <!-- Scholarship Consideration -->
      <div v-if="showScholarshipQuestion">
        <h2 class="block text-sm font-medium text-indigo-base flex justify-between mb-1">
          Would you like to be considered for scholarships?
        </h2>
        <RadioButtons
          v-model="form.consideredForScholarships"
          data-testid="considered-for-scholarships"
          :group-name="'consideredForScholarships'"
          :options="scholarshipOptions"
          groupName="consideredForScholarships"
          @option-choosed="(value) => (form.consideredForScholarships = value)"
          :container-class="'flex justify-start space-x-4'"
        />
      </div>
      <!-- FAFSA Certification Statement -->
      <div v-if="showScholarshipQuestion && form.consideredForScholarships === 'yes'">
        <CheckBox
          data-testid="certify-FAFSA"
          id="certifyFAFSA"
          v-model="form.certifyFAFSA"
          :vuelidate-field="v$.sameAsPermanentAddress"
        >
          <template #label>
            I certify that I complete a FAFSA form annually and have demonstrated <br />
            eligibility for financial aid.
          </template>
        </CheckBox>
      </div>

      <!-- School Standing -->
      <div v-if="showSchoolStanding">
        <h1 class="font-bold text-[28px] uppercase">
          School Standing
        </h1>
        <p class="font-medium text-xs pb-4">
          Please answer truthfully. We may require additional documentation.
        </p>
        <!-- Displinary sanctions -->
        <SingleSelect
          id="hadDisplinarySanctions"
          v-model.trim="form.hadDisciplinarySanctions"
          data-testid="hadDisplinarySanctions"
          label="During any learning experience, have you ever had any disciplinary or academic sanctions?*"
          label-class="md:whitespace-nowrap"
          :vuelidate-field="v$.hadDisciplinarySanctions"
          :options="displinarySanctionsOptions"
        />
      </div>

      <!-- Address(es) -->
      <div v-if="showAddress" class="grid grid-cols-1 gap-y-7">
        <div>
          <h1 class="font-bold text-[28px] uppercase">
            Address(es)
          </h1>
          <p class="font-medium text-xs">
            For sending important documents and visa advising.
          </p>
        </div>

        <!-- Current Address -->
        <div>
          <AddressComponent
            v-if="!addressController.currentAddress.showManualForm"
            id="currentAddress"
            data-testid="currentAddress"
            label="Current Address*"
            template-name="v3"
            :vuelidate-field="v$.currentAddress"
            :initial-address-info="initialCurrentAddress"
            @update-address-info="
              (addressInformation) => {
                updateAddress('currentAddress', addressInformation);
              }
            "
          />
          <!-- User will input his own current address -->
          <ManualAddressForm
            v-if="addressController.currentAddress.showManualForm"
            :initial-countries="countries"
            :parent-object-label="'Current Address'"
            :show-error="v$.currentAddress.$invalid"
            :initial-address="form.currentAddress.address_line_1"
            :initial-postal-code="form.currentAddress.address_postal_code"
            :initial-city="form.currentAddress.address_city"
            :initial-state="form.currentAddress.address_state"
            :initial-country-id="form.currentAddress.address_country_id"
            :initial-country-label="form.currentAddress.country"
            @update-address-object="(addressInformation) => updateAddressManually('currentAddress', addressInformation)"
          />
        </div>

        <!-- Permanent Address -->
        <div>
          <label for="permanentAddress" class="block text-sm text-indigo-base font-bold mb-17px">
            <span class="font-medium flex justify-between mb-1">
              Permanent Address*
            </span>
          </label>
          <!-- CheckBox for same address using the provided CheckBox component -->
          <CheckBox id="sameAsPermanentAddress" v-model="isSameAddress" :vuelidate-field="v$.sameAsPermanentAddress">
            <template #label>
              Same as current address
            </template>
          </CheckBox>
          <AddressComponent
            v-if="!isSameAddress && !addressController.permanentAddress.showManualForm"
            id="permanentAddress"
            data-testid="permanentAddress"
            template-name="v3"
            :vuelidate-field="v$.permanentAddress"
            :initial-address-info="initialPermanentAddress"
            @update-address-info="
              (addressInformation) => {
                updateAddress('permanentAddress', addressInformation);
              }
            "
          />

          <div v-if="addressController.permanentAddress.showManualForm && !isSameAddress" class="grid grid-cols-1 gap-y-7 pt-10">
            <!-- User will input his own permanent address -->
            <ManualAddressForm
              :initial-countries="countries"
              :parent-object-label="'Permanent Address'"
              :show-error="v$.permanentAddress.$invalid"
              :initial-address="form.permanentAddress.address_line_1"
              :initial-postal-code="form.permanentAddress.address_postal_code"
              :initial-city="form.permanentAddress.address_city"
              :initial-state="form.permanentAddress.address_state"
              :initial-country-id="form.permanentAddress.address_country_id"
              :initial-country-label="form.permanentAddress.country"
              @update-address-object="
                (addressInformation) => updateAddressManually('permanentAddress', addressInformation)
              "
            />
          </div>
        </div>

        <!--  Billing Address -->
        <label v-if="contractNumber" for="billingAddress" class="block text-sm text-indigo-base font-bold">
          <span class="font-medium flex justify-between mb-17px">
            Billing Address*
          </span>

          <RadioButtons
            v-model="billingAddressSameAs"
            :container-class="`grid grid-cols-1 md:grid-cols-3 gap-x-7 ${hideBillingAddressInput ? '' : 'mb-17px'}`"
            :options="addressOptions"
            :group-name="'address'"
            @option-choosed="preSelectAddress"
          />
          <AddressComponent
            v-if="contractNumber && !hideBillingAddressInput && !addressController.billingAddress.showManualForm"
            id="billingAddress"
            data-testid="billingAddress"
            template-name="v3"
            :vuelidate-field="v$.billingAddress"
            :initial-address-info="initialBillingAddress"
            @update-address-info="
              (addressInformation) => {
                updateAddress('billingAddress', addressInformation);
              }
            "
          />
          <div class="grid grid-cols-1 gap-y-7 font-normal">
            <!-- User will input his own billing address -->
            <ManualAddressForm
              v-if="addressController.billingAddress.showManualForm && !hideBillingAddressInput"
              :initial-countries="countries"
              :parent-object-label="'Billing Address'"
              :show-error="v$.billingAddress.$invalid"
              :initial-address="form.billingAddress.address_line_1"
              :initial-postal-code="form.billingAddress.address_postal_code"
              :initial-city="form.billingAddress.address_city"
              :initial-state="form.billingAddress.address_state"
              :initial-country-id="form.billingAddress.address_country_id"
              :initial-country-label="form.billingAddress.country"
              @update-address-object="
                (addressInformation) => updateAddressManually('billingAddress', addressInformation)
              "
            />
          </div>
        </label>
      </div>

      <!-- Parent Guardians -->
      <div v-if="isUnderage">
        <h1 class="font-bold text-[28px] uppercase">
          Parent/Guardian Information
        </h1>
        <p class="font-medium text-xs">
          Since you’re under 18 you’re required to share the name of a parent/guardian and their email address. They
          will be copied on all correspondence and be required to sign all legal documents.
        </p>
      </div>

      <!-- Parent Guardian name -->
      <TextField
        v-if="isUnderage"
        id="parentName"
        v-model.trim="form.parentGuardianName"
        data-testid="parentName"
        label="Parent/Guardian Name*"
        placeholder="Enter Parent/Guardian Name"
        :vuelidate-field="v$.parentGuardianName"
        :show-errors="true"
      />

      <!-- Parent Guardian email -->
      <TextField
        v-if="isUnderage"
        id="parentEmail"
        v-model.trim="form.parentGuardianEmail"
        data-testid="parentEmail"
        label="Parent/Guardian Email*"
        placeholder="Enter Parent/Guardian Email"
        :vuelidate-field="v$.parentGuardianEmail"
        :show-errors="true"
      />

      <ButtonWithSpinner
        id="learnerInfoButton"
        ref="learnerInfoSubmit"
        data-testid="learnerInfoButton"
        data-cy="learnerInfoButton"
        type="submit"
        variant="secondary"
        variant-type="block"
        button-height="min-h-[58px] md:min-h-[60px] mt-4"
        :grey-disabled-class="true"
        :disabled="v$.$invalid"
        @click="submitLearnerInfo"
      >
        <div class="flex items-center justify-center">
          <span class="pr-3 uppercase">
            Continue
          </span>
          <ArrowRight custom-class="w-4 h-4" />
        </div>
      </ButtonWithSpinner>
      <div v-if="submissionError" class="error text-error-900 mt-2">
        {{ submissionError }}
      </div>
    </div>
  </div>
</template>
